<template>
  <label class="radio-button" :class="{ 'radio-button--disabled': disabled, [`radio-button--${size}`]: true }">
    <input
      class="radio-button__input"
      type="radio"
      v-bind="{ name: group, value, checked, disabled }"
      @change="handleChange"
    />
    <span class="radio-button__check" />
    {{ label }}
  </label>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'RadioButton',
  props: {
    size: {
      type: String,
      default: 'md',
      validator: (value: string) => ['sm', 'md'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    group: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    checked: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    handleChange(e: Event) {
      const el = e.target as HTMLInputElement;
      const { label, group, value } = this;
      this.$emit('change', { label, group, value, checked: el.checked });
    },
  },
});
</script>

<style lang="scss" scoped>
.radio-button {
  $this: &;

  @include body;

  position: relative;
  cursor: pointer;
  color: var(--theme-color-text);
  display: flex;

  &--disabled {
    cursor: default;
    // As in design system, but we probably will need to override it

    color: var(--gray-50);
  }

  &__input {
    position: absolute;
    z-index: -1;
    width: 0;
    height: 0;
    opacity: 0;
  }

  &__check {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border: 1px solid var(--theme-color-border);
    border-radius: 50%;
    transition-property: border-color, border-width, outline-color;
    transition-duration: 0.2s;
    background: var(--theme-color-card);
    outline-offset: 2px;
    outline: 2px solid transparent;
    margin-right: 6px;

    #{$this}--sm & {
      width: 16px;
      height: 16px;
      align-self: center;
    }
  }

  &__input:checked ~ &__check {
    border-color: var(--blue-60);
    border-width: 6px;

    #{$this}--sm & {
      border-width: 4px;
    }
  }

  &__input:focus-visible ~ &__check {
    outline-color: var(--blue-60);
  }

  &__input:disabled ~ &__check {
    border-color: var(--theme-color-border);
    background: var(--theme-color-over-card);
    cursor: default;
  }

  &__input:checked:disabled ~ &__check {
    border-color: var(--theme-color-over-card);
    background: var(--theme-color-border);
    outline: 1px solid var(--theme-color-border);
    outline-offset: -1px;
    transition: none;
  }

  &:hover #{$this}__input:not([disabled]) ~ #{$this}__check {
    border-color: var(--blue-60);
  }
}
</style>
